import React, { useCallback, useContext, useMemo, useState } from 'react';

import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import ToastContext from 'contexts/ToastContext';

import deleteAdditionalPayment from '../../api/deleteAdditionalPayment';
import postAdditionalPayment from '../../api/postAdditionalPayment';
import useAdditionalPaymentPresets from '../../hooks/useAdditionalPaymentPresets';
import {
  AdditionalPaymentsView,
  BonusOverviewContext,
} from '../../providers/BonusOverviewProvider';
import useAdditionalPayments from './useAdditionalPayments';
import useFormatPaymentAmount from './useFormatPaymentAmount';
import { getAdditionalPaymentErrorMessage, getMaxAddableAmount } from './utils';

interface AdditionalPaymentsModeContextType {
  view: AdditionalPaymentsView;
  // The record's payments + the caps for its period (GET). Undefined while
  // loading or when the read failed.
  response: BonusTypes.AdditionalPayments.Response | undefined;
  isLoading: boolean;
  hasFailed: boolean;
  serverError: string | undefined;
  // Add
  availablePresets: BonusOverrideSettings.AdditionalPaymentPreset[];
  selectedPreset: BonusOverrideSettings.AdditionalPaymentPreset | undefined;
  customAmount: number | undefined;
  note: string;
  // The label sent with the payment: the chosen quick option's, if any.
  label: string | undefined;
  // A note is needed unless the payment carries a label (API: note or label).
  isNoteRequired: boolean;
  selectPreset: (preset: BonusOverrideSettings.AdditionalPaymentPreset) => void;
  setCustomAmount: (amount: number | undefined) => void;
  setNote: (note: string) => void;
  amountError: string | undefined;
  // The chosen amount when it passes the caps.
  validAmount: number | undefined;
  maxAddable: number;
  isSaving: boolean;
  isAddDisabled: boolean;
  addPayment: () => Promise<void>;
  // Manage
  removingPaymentId: string | undefined;
  removePayment: (paymentId: string) => Promise<void>;
}

const missingFunc = () => {
  const error = new Error();
  error.name = 'AdditionalPaymentsMode: Function is undefined';
  throw error;
};

export const AdditionalPaymentsModeContext =
  React.createContext<AdditionalPaymentsModeContextType>({
    view: 'add',
    response: undefined,
    isLoading: true,
    hasFailed: false,
    serverError: undefined,
    availablePresets: [],
    selectedPreset: undefined,
    customAmount: undefined,
    note: '',
    label: undefined,
    isNoteRequired: true,
    selectPreset: missingFunc,
    setCustomAmount: missingFunc,
    setNote: missingFunc,
    amountError: undefined,
    validAmount: undefined,
    maxAddable: 0,
    isSaving: false,
    isAddDisabled: true,
    addPayment: () => Promise.resolve(),
    removingPaymentId: undefined,
    removePayment: () => Promise.resolve(),
  });

/**
 * State for the slideout's recognition payments mode, shared by its header
 * (the add / done actions) and body (the form or list). Wraps both while
 * BonusOverviewContext's mode is 'additionalPayments'.
 */
const AdditionalPaymentsModeProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { api } = useContext(CloudFunctionClientContext);
  const { showToast } = useContext(ToastContext);
  const {
    bonusOverview,
    currentMode,
    exitAdditionalPaymentsMode,
    refreshBonusOverview,
  } = useContext(BonusOverviewContext);
  const formatAmount = useFormatPaymentAmount();
  const view = currentMode.additionalPaymentsView ?? 'add';
  const bonusDatasetRecordId =
    bonusOverview?.actualRewardOutput.bonusDatasetRecordId;
  const { response, setResponse, isLoading, hasFailed } = useAdditionalPayments(
    { bonusDatasetRecordId },
  );
  const presets = useAdditionalPaymentPresets({
    bonusProgramId: bonusOverview?.bonusProgram.id ?? '',
  });

  const [selectedPreset, setSelectedPreset] = useState<
    BonusOverrideSettings.AdditionalPaymentPreset | undefined
  >();
  const [customAmount, setCustomAmountState] = useState<number | undefined>();
  const [note, setNote] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [removingPaymentId, setRemovingPaymentId] = useState<
    string | undefined
  >();
  const [serverError, setServerError] = useState<string | undefined>();

  const maxAddable = response ? getMaxAddableAmount(response) : 0;
  const availablePresets = useMemo(
    () => presets.filter((preset) => preset.amount <= maxAddable),
    [maxAddable, presets],
  );
  const amount = customAmount ?? selectedPreset?.amount;
  // A custom ("Other") amount has no label, so it needs a note.
  const label =
    customAmount === undefined && selectedPreset?.label
      ? selectedPreset.label
      : undefined;
  const isNoteRequired = label === undefined;

  const amountError = (() => {
    if (amount === undefined || !response?.policy) {
      return undefined;
    }
    if (amount <= 0) {
      return 'Amount must be greater than zero';
    }
    if (amount > response.policy.maxSinglePayment) {
      return `A single payment can be at most ${formatAmount(
        response.policy.maxSinglePayment,
      )}`;
    }
    if (response.remaining !== null && amount > response.remaining) {
      return `Only ${formatAmount(
        response.remaining,
      )} is left under this period's limit`;
    }
    return undefined;
  })();

  const validAmount =
    amount !== undefined && amount > 0 && !amountError ? amount : undefined;
  const isAddDisabled =
    validAmount === undefined ||
    (isNoteRequired && note.trim() === '') ||
    isSaving;

  const selectPreset = useCallback(
    (preset: BonusOverrideSettings.AdditionalPaymentPreset) => {
      setSelectedPreset(preset);
      setCustomAmountState(undefined);
    },
    [],
  );

  const setCustomAmount = useCallback((value: number | undefined) => {
    setCustomAmountState(value);
    setSelectedPreset(undefined);
  }, []);

  const addPayment = useCallback(async () => {
    if (!bonusOverview || validAmount === undefined || isAddDisabled) {
      return;
    }

    setIsSaving(true);
    setServerError(undefined);
    const r = await postAdditionalPayment({
      api,
      driver: bonusOverview.driver,
      bonusProgramId: bonusOverview.bonusProgram.id,
      bonusDatasetRecordId:
        bonusOverview.actualRewardOutput.bonusDatasetRecordId,
      amount: validAmount,
      note: note.trim() || undefined,
      label,
    });
    setIsSaving(false);

    if (!r.ok) {
      setServerError(getAdditionalPaymentErrorMessage(r.data));
      return;
    }

    showToast('Additional payment added');
    exitAdditionalPaymentsMode();
    refreshBonusOverview();
  }, [
    api,
    bonusOverview,
    exitAdditionalPaymentsMode,
    isAddDisabled,
    label,
    note,
    refreshBonusOverview,
    showToast,
    validAmount,
  ]);

  const removePayment = useCallback(
    async (paymentId: string) => {
      if (!bonusOverview) {
        return;
      }

      setRemovingPaymentId(paymentId);
      setServerError(undefined);
      const r = await deleteAdditionalPayment({
        api,
        driver: bonusOverview.driver,
        bonusProgramId: bonusOverview.bonusProgram.id,
        bonusDatasetRecordId:
          bonusOverview.actualRewardOutput.bonusDatasetRecordId,
        paymentId,
      });
      setRemovingPaymentId(undefined);

      if (!r.ok) {
        setServerError(getAdditionalPaymentErrorMessage(r.data));
        return;
      }

      showToast('Additional payment removed');
      if (r.data) {
        setResponse(r.data);
        // Nothing left to manage.
        if (r.data.payments.length === 0) {
          exitAdditionalPaymentsMode();
        }
      }
      refreshBonusOverview();
    },
    [
      api,
      bonusOverview,
      exitAdditionalPaymentsMode,
      refreshBonusOverview,
      setResponse,
      showToast,
    ],
  );

  return (
    <AdditionalPaymentsModeContext.Provider
      value={{
        view,
        response,
        isLoading,
        hasFailed,
        serverError,
        availablePresets,
        selectedPreset,
        customAmount,
        note,
        label,
        isNoteRequired,
        selectPreset,
        setCustomAmount,
        setNote,
        amountError,
        validAmount,
        maxAddable,
        isSaving,
        isAddDisabled,
        addPayment,
        removingPaymentId,
        removePayment,
      }}
    >
      {children}
    </AdditionalPaymentsModeContext.Provider>
  );
};

export default AdditionalPaymentsModeProvider;
